/* eslint-disable camelcase */
import { createSelector } from 'reselect';
import { apiFetch } from 'modules/shared/fetch';
import Errors from 'modules/shared/error/errors';
import { formatAmount, getDateAndTimeAsNumbers } from 'utils';

const prefix = 'dev-portal/billing/payments';
// Actions
const actionTypes = {
  GET_BILLING_PAYMENTS_STARTED: `${prefix}/GET_BILLING_PAYMENTS_STARTED`,
  GET_BILLING_PAYMENTS_SUCCESS: `${prefix}/GET_BILLING_PAYMENTS_SUCCESS`,
  GET_BILLING_PAYMENTS_ERROR: `${prefix}/GET_BILLING_PAYMENTS_ERROR`,

  GET_DOWNLOADABLE_CSV_DATA_STARTED: `${prefix}/GET_DOWNLOADABLE_CSV_DATA_STARTED`,
  GET_DOWNLOADABLE_CSV_DATA_SUCCESS: `${prefix}/GET_DOWNLOADABLE_CSV_DATA_SUCCESS`,
  GET_DOWNLOADABLE_CSV_DATA_ERROR: `${prefix}/GET_DOWNLOADABLE_CSV_DATA_ERROR`,
};

// Reducer
const initialState = {
  loading: false,
  items: [],
  entries_total: 0,
  csvData: [],
  loadingCSVData: false,
};

export default function reducer(state = initialState, { type, payload }) {
  if (type === actionTypes.GET_BILLING_PAYMENTS_STARTED) {
    return {
      ...state,
      loading: true,
    };
  }

  if (type === actionTypes.GET_BILLING_PAYMENTS_SUCCESS) {
    return {
      ...state,
      loading: false,
      ...payload,
    };
  }

  if (type === actionTypes.GET_BILLING_PAYMENTS_ERROR) {
    return {
      ...initialState,
    };
  }

  if (type === actionTypes.GET_DOWNLOADABLE_CSV_DATA_STARTED) {
    return {
      ...state,
      loadingCSVData: true,
    };
  }

  if (type === actionTypes.GET_DOWNLOADABLE_CSV_DATA_SUCCESS) {
    return {
      ...state,
      loadingCSVData: false,
      csvData: payload,
    };
  }

  if (type === actionTypes.GET_DOWNLOADABLE_CSV_DATA_ERROR) {
    return {
      ...initialState,
    };
  }

  return state;
}

// Action Creators
const doGetBillingPayments = (appToken, body) => async (dispatch, getState) => {
  try {
    dispatch({
      type: actionTypes.GET_BILLING_PAYMENTS_STARTED,
    });

    const { environment } = getState().root.global;
    const response = await getBillingPayments(appToken, body, {
      appEnvironment: environment,
    });

    dispatch({
      type: actionTypes.GET_BILLING_PAYMENTS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    Errors.handle(error);

    dispatch({
      type: actionTypes.GET_BILLING_PAYMENTS_ERROR,
    });
  }
};

// eslint-disable-next-line consistent-return
const doGetCSVData = (appToken, body) => async (dispatch, getState) => {
  try {
    dispatch({
      type: actionTypes.GET_DOWNLOADABLE_CSV_DATA_STARTED,
    });

    const { environment } = getState().root.global;
    const response = await getBillingPayments(appToken, body, {
      appEnvironment: environment,
    });

    dispatch({
      type: actionTypes.GET_DOWNLOADABLE_CSV_DATA_SUCCESS,
      payload: response.items,
    });

    return response.items;
  } catch (error) {
    Errors.handle(error);

    dispatch({
      type: actionTypes.GET_DOWNLOADABLE_CSV_DATA_ERROR,
    });
  }
};

export const actions = {
  doGetBillingPayments,
  doGetCSVData,
};

// APIs
/**
 * POST /billing/applications/:appToken/payments
 * @param {string} appToken
 * @param {object} body
 * @param {string} appEnvironment
 */
const getBillingPayments = async (appToken, body, { appEnvironment }) => {
  const response = await apiFetch(
    `/billing/applications/${appToken}/payments`,
    { method: 'POST', body, appEnvironment },
  );

  return response.data;
};

// Selectors
const selectPayments = state => state.root.billing.payments;

const selectItems = createSelector([selectPayments], payments => {
  const { loading, ...rest } = payments;
  return rest;
});

const selectTableViewData = createSelector([selectItems], payments =>
  payments.items.map(payment => {
    const {
      initiation_timestamp,
      payment_intent_id,
      amount,
      currency,
      payment_id,
    } = payment;
    const formattedAmount = formatAmount(amount, currency);
    const timestampAsDate = new Date(initiation_timestamp);
    const { date, time } = getDateAndTimeAsNumbers(timestampAsDate);
    const timestamp = `${time}  ${date}`;
    return {
      'Payment Intent ID': { payment_intent_id },
      Amount: { timestamp, amount: formattedAmount },
      hidden: { payment_id },
    };
  }),
);

const selectCSVData = createSelector(
  [selectPayments],
  payments => payments.csvData,
);

const selectLoadingCSVData = createSelector(
  [selectPayments],
  payments => payments.loadingCSVData,
);

const selectLoading = createSelector(
  [selectPayments],
  payments => payments.loading,
);

export const selectors = {
  selectItems,
  selectLoading,
  selectTableViewData,
  selectCSVData,
  selectLoadingCSVData,
};
